// useUserRating.js - COMPLETE FIXED VERSION
import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { userRatingApi } from '../../src/services/userRatingApi';

// ✅ ADD THIS HELPER FUNCTION AT THE TOP
const isCancellationError = (error) => {
  return error && (error.cancelled === true || error.message?.includes('cancelled'));
};

// Hook for fetching all users rating summary
export const useUsersRatingSummary = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    viewMode: 'all',
    period: null,
    year: null
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching users with filters:', filters);
      const data = await userRatingApi.getAllUsersRatingSummary(
        filters.viewMode,
        filters.period,
        filters.year
      );
      setUsers(data);
      setLoading(false);
    } catch (err) {
      // ✅ ADDED: Check for cancellation
      if (isCancellationError(err)) {
        console.log('Users request was cancelled, ignoring error');
        setLoading(false);
        return;
      }
      
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to fetch users');
      setLoading(false);
      message.error('Failed to load users data');
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const refetch = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { users, loading, error, refetch, updateFilters, filters };
};

// Hook for fetching user details
export const useUserDetails = (userId) => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    viewMode: 'all',
    period: null,
    year: null
  });

  const fetchUserDetails = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching user details with filters:', filters);
      const data = await userRatingApi.getUserRatingDetails(
        userId,
        filters.viewMode,
        filters.period,
        filters.year
      );
      setUserDetails(data);
      setLoading(false);
    } catch (err) {
      // ✅ ADDED: Check for cancellation
      if (isCancellationError(err)) {
        console.log('User details request was cancelled, ignoring error');
        setLoading(false);
        return;
      }
      
      console.error('Error fetching user details:', err);
      setError(err.message || `Failed to fetch user details for user ${userId}`);
      setLoading(false);
    }
  }, [userId, filters]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  const updateFilters = useCallback((newFilters) => {
    console.log('Updating user details filters:', newFilters);
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const refetch = useCallback(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  return { userDetails, loading, error, refetch, updateFilters };
};

// Hook for fetching performance data
export const usePerformanceData = (userId, viewMode = 'monthly', year = null) => {
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPerformanceData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await userRatingApi.getUserPerformanceData(userId, viewMode, year);
      setPerformanceData(data);
      setLoading(false);
    } catch (err) {
      // ✅ ADDED: Check for cancellation
      if (isCancellationError(err)) {
        console.log('Performance data request was cancelled, ignoring error');
        setLoading(false);
        return;
      }
      
      console.error('Error fetching performance data:', err);
      setError(err.message || 'Failed to fetch performance data');
      setLoading(false);
    }
  }, [userId, viewMode, year]);

  useEffect(() => {
    fetchPerformanceData();
  }, [fetchPerformanceData]);

  const refetch = useCallback(() => {
    fetchPerformanceData();
  }, [fetchPerformanceData]);

  return { performanceData, loading, error, refetch };
};

// Hook for fetching user tasks
export const useUserTasks = (userId) => {
  const [tasksData, setTasksData] = useState({ tasks: [], totalCount: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 0,
    size: 10,
    clientName: '',
    projectName: '',
    taskName: '',
    performance: '',
    proofreading: '',
    quality: ''
  });

  const fetchTasks = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await userRatingApi.getUserTasks(userId, filters);
      setTasksData(data);
      setLoading(false);
    } catch (err) {
      // ✅ ADDED: Check for cancellation
      if (isCancellationError(err)) {
        console.log('Tasks request was cancelled, ignoring error');
        setLoading(false);
        return;
      }
      
      console.error('Error fetching tasks:', err);
      setError(err.message || 'Failed to fetch tasks');
      setLoading(false);
    }
  }, [userId, filters]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const updatePagination = useCallback((page, size) => {
    setFilters(prev => ({ ...prev, page, size }));
  }, []);

  const refetch = useCallback(() => {
    fetchTasks();
  }, [fetchTasks]);

  return { tasksData, loading, error, updateFilters, updatePagination, refetch };
};

// Hook for fetching communication data
export const useCommunicationData = (userId, period = null, viewMode = 'monthly') => {
  const [communicationData, setCommunicationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCommunicationData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await userRatingApi.getUserCommunicationData(userId, period, viewMode);
      setCommunicationData(data);
      setLoading(false);
    } catch (err) {
      // ✅ ADDED: Check for cancellation
      if (isCancellationError(err)) {
        console.log('Communication data request was cancelled, ignoring error');
        setLoading(false);
        return;
      }
      
      console.error('Error fetching communication data:', err);
      setError(err.message || 'Failed to fetch communication data');
      setLoading(false);
    }
  }, [userId, period, viewMode]);

  useEffect(() => {
    fetchCommunicationData();
  }, [fetchCommunicationData]);

  const refetch = useCallback(() => {
    fetchCommunicationData();
  }, [fetchCommunicationData]);

  return { communicationData, loading, error, refetch };
};

// Hook for fetching skills data
export const useSkillsData = (userId, period = null, viewMode = 'monthly') => {
  const [skillsData, setSkillsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSkillsData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await userRatingApi.getUserSkillsData(userId, period, viewMode);
      setSkillsData(data);
      setLoading(false);
    } catch (err) {
      // ✅ ADDED: Check for cancellation
      if (isCancellationError(err)) {
        console.log('Skills data request was cancelled, ignoring error');
        setLoading(false);
        return;
      }
      
      console.error('Error fetching skills data:', err);
      setError(err.message || 'Failed to fetch skills data');
      setLoading(false);
    }
  }, [userId, period, viewMode]);

  useEffect(() => {
    fetchSkillsData();
  }, [fetchSkillsData]);

  const refetch = useCallback(() => {
    fetchSkillsData();
  }, [fetchSkillsData]);

  return { skillsData, loading, error, refetch };
};

// Hook for fetching task filter options
export const useTaskFilterOptions = (userId) => {
  const [filterOptions, setFilterOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFilterOptions = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await userRatingApi.getTaskFilterOptions(userId);
      setFilterOptions(data);
      setLoading(false);
    } catch (err) {
      // ✅ ADDED: Check for cancellation
      if (isCancellationError(err)) {
        console.log('Filter options request was cancelled, ignoring error');
        setLoading(false);
        return;
      }
      
      console.error('Error fetching filter options:', err);
      setError(err.message || 'Failed to fetch filter options');
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const refetch = useCallback(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  return { filterOptions, loading, error, refetch };
};

// Hook for fetching tasks summary with date range
export const useTasksSummary = (startDate, endDate, userId) => {
  const [summaryData, setSummaryData] = useState({
    overallSummary: null,
    tasks: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching tasks summary with:', { startDate, endDate, userId });
      const data = await userRatingApi.getTasksSummaryWithDateRange(startDate, endDate, userId);
      console.log('Tasks summary data received:', data);
      setSummaryData(data);
      setLoading(false);
    } catch (err) {
      // ✅ ADDED: Check for cancellation
      if (isCancellationError(err)) {
        console.log('Tasks summary request was cancelled, ignoring error');
        setLoading(false);
        return;
      }
      
      console.error('Error fetching tasks summary:', err);
      setError(err.message || 'Failed to fetch tasks summary');
      setLoading(false);
    }
  }, [startDate, endDate, userId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const refetch = useCallback(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summaryData, loading, error, refetch };
};